import { OfflineQueueItem, OfflineActionType } from '../../types/technicianJob';

const STORAGE_KEY = 'nad_offline_queue';

export class OfflineQueueManager {
  private listeners: (() => void)[] = [];

  constructor() {
    window.addEventListener('online', () => this.flushPending());
  }

  enqueue(jobId: string, action: OfflineActionType, payload: unknown): OfflineQueueItem {
    const item: OfflineQueueItem = {
      id: `OQ-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      job_id: jobId,
      action,
      payload,
      queued_at: new Date().toISOString(),
      retry_count: 0,
    };
    const queue = this.getAll();
    queue.push(item);
    this.save(queue);
    this.notify();
    return item;
  }

  getAll(): OfflineQueueItem[] {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  }

  remove(id: string): void {
    const queue = this.getAll().filter(item => item.id !== id);
    this.save(queue);
    this.notify();
  }

  clear(): void {
    localStorage.removeItem(STORAGE_KEY);
    this.notify();
  }

  getPendingCount(): number {
    return this.getAll().length;
  }

  flushPending(): void {
    if (!navigator.onLine) return;
    const queue = this.getAll();
    if (queue.length === 0) return;
    this.clear();
    // In production, each item would be sent to the backend adapter sequentially.
    // For now, we clear the queue and notify listeners.
    this.notify();
  }

  onChange(listener: () => void): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  private save(queue: OfflineQueueItem[]): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(queue));
  }

  private notify(): void {
    this.listeners.forEach(l => l());
  }
}

export const offlineQueue = new OfflineQueueManager();
